import {useMemo} from 'external/react'
import {Spec, useImmutableState} from 'external/immutability-helper'

export type ValidationResult<T> = [keyof T, string | string[] | false]

export type Validation<T> = (state:Partial<T>) => ValidationResult<T>

export type ValidationFunction<T> = (state:Partial<T>, submitted?:boolean) => boolean 

export type ValidationMap<T> = {
    [Property in keyof T]: Array<Validation<T>>
}

export default function useFormValidation<T>(validationMap: ValidationMap<T>) {
   
    type FieldErrorsTemplate = {
        [Property in keyof T]: ValidationResult<T>
    }

    type ErrorsTemplate = FieldErrorsTemplate & {
        notEmpty: boolean
        submitted: boolean
    }
    
    type FieldErrorsUpdate = {
        [Property in keyof T]?: {$set: ValidationResult<T>[1]}
    }
    
    type ErrorsUpdate = FieldErrorsUpdate & {
        notEmpty?: {$set: boolean},
        submitted: {$apply: (current: boolean) => boolean}
    }

    const errorsTemplate:ErrorsTemplate = Object.keys(validationMap).reduce((errors, field) => {
        errors[field] = false
        return errors
    }, {notEmpty: false, submitted: false} as ErrorsTemplate)

    
    const {state, update} = useImmutableState(errorsTemplate)

    const errors = state as ErrorsTemplate
    
    const validations = useMemo(() => Object.entries(validationMap) as Array<[keyof T, Array<Validation<T>>]>, [validationMap])

    function validate (state: Partial<T>, submitted?: boolean) : boolean {
            
            let isValid = true
            const errorUpdates:ErrorsUpdate = validations.reduce((errorUpdates:any, [fieldName, fieldValidations])=> {
                let isFieldValid = true
                for (const validation of fieldValidations) {
                    const [field, validationResult] = validation(state)
                    if (validationResult !== false) {
                        isValid = false
                        isFieldValid = false
                        if (errorUpdates[field]) {
                            if (!Array.isArray(errorUpdates[field].$set)) {
                                errorUpdates[field].$set = [errorUpdates[field].$set]
                            }
                            errorUpdates[field].$set.push(validationResult)
                        } else {
                            errorUpdates[field] = {$set: validationResult}
                        }
                    }
                }
                if (isFieldValid) {
                    errorUpdates[fieldName] = {$set: false}
                }
                return errorUpdates
            }, {submitted: {$apply: (current: boolean) => current || submitted === true}} as ErrorsUpdate)

           if (!isValid) {
               errorUpdates.notEmpty = {$set: !isValid}
           }

           update(errorUpdates as Spec<ErrorsTemplate>)
           return isValid
    }
    
    function reset() {
        update({$shallow: errorsTemplate})
    }

    return [errors, validate, reset] as [ErrorsTemplate, ValidationFunction<T>, () => void]
}